import { useMemo, useState } from "react";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Loading from "components/Loading";

import councilService from "api/councilService";
import { ICouncilLegalActOption } from "dto/Council/ICouncil";

import styles from "./styles.module.scss";

interface Props {
  sessionId: string;
  /** Akty już dołączone w tej sesji — pokazujemy je wyszarzone, żeby nie dublować. */
  attachedIds: string[];
  onClose: () => void;
  onApply: (acts: ICouncilLegalActOption[]) => void;
}

// Dołączenie projektu aktu do punktu porządku obrad — odwzorowanie sekcji „do przyjęcia"
// z modala załączników w Magistracie (AddAttachmentViewModel). Lista to projekty uchwał
// z modułu prawa miejscowego; backend odsiewa te przypięte już do innej sesji.
//
// Dołączenie aktu jest RELACJĄ, nie kopią pliku: w punkcie widnieje odnośnik do aktu,
// a gdy prowadzący zarządzi w tym punkcie głosowanie, może wskazać ten akt jako przedmiot
// głosowania — i to powiązanie widać potem w publicznym rejestrze.

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "";

const AttachLegalActModal = ({ sessionId, attachedIds, onClose, onApply }: Props) => {
  const { data, isLoading } = councilService.useGetLegalActOptions(sessionId);

  const [selected, setSelected] = useState<string[]>([]);
  const [text, setText] = useState("");

  const options = useMemo(() => data?.data ?? [], [data]);

  const filtered = useMemo(() => {
    const needle = text.trim().toLowerCase();

    if (!needle) return options;

    return options.filter(
      (o) =>
        (o.Title ?? "").toLowerCase().includes(needle) ||
        (o.ActNumber ?? "").toLowerCase().includes(needle),
    );
  }, [options, text]);

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );

  const apply = () => {
    const picked = options.filter((o) => selected.includes(o.Id));

    if (picked.length) onApply(picked);
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={`${styles.modal} ${styles.modalWide}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.modalTitle}>Dołącz projekt uchwały</div>

        <div className={styles.modalBody}>
          <input
            className={styles.textInput}
            value={text}
            placeholder="Szukaj po tytule lub numerze"
            onChange={(e) => setText(e.target.value)}
          />

          {isLoading && <Loading />}

          {!isLoading &&
            filtered.map((option) => {
              const already = attachedIds.includes(option.Id);

              return (
                <label
                  key={option.Id}
                  className={styles.checkboxRow}
                  title={already ? "Ten akt jest już dołączony w tej sesji" : undefined}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(option.Id)}
                    disabled={already}
                    onChange={() => toggle(option.Id)}
                  />
                  <span>
                    {option.ActNumber
                      ? `${option.ActTypeName} Nr ${option.ActNumber}`
                      : option.ActTypeName}
                    <div className={styles.fieldLabel}>
                      {option.Title}
                      {option.Date ? ` · ${formatDate(option.Date)}` : ""}
                      {already ? " · już dołączony" : ""}
                    </div>
                  </span>
                </label>
              );
            })}

          {!isLoading && !filtered.length && (
            <span className={styles.fieldLabel}>
              {text
                ? "Nie znaleziono projektów pasujących do szukanego tekstu."
                : "Brak projektów uchwał gotowych do dołączenia. Projekt musi być zaakceptowany i nieprzypisany do innej sesji."}
            </span>
          )}
        </div>

        <div className={styles.modalFooter}>
          <HollowButton text="Anuluj" onClick={onClose} />
          <Button text="Załącz" disabled={!selected.length} onClick={apply} />
        </div>
      </div>
    </div>
  );
};

export default AttachLegalActModal;
